import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Loader2, Plus, X } from "lucide-react"
import { useState } from "react"
import { AddSupplierEmailModal } from "./AddSupplierEmailModal"

interface RecipientEditorProps {
  to: string[]
  cc: string[]
  setTo: (next: string[]) => void
  setCc: (next: string[]) => void
  supplierId?: string
  supplierName?: string
  supplierNumber?: string
}

// Same shape used elsewhere in the app (see VersandComposer).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Exported so bulk-mode's RecipientList can reuse the same chip editor per supplier
// row instead of the read-only single-address display it used to have.
export function Row({
  label,
  list,
  onAdd,
  onRemove,
  onAddMissingMasterDataEmail,
  pendingAddress,
  compact,
}: {
  // Omitted (compact mode) to drop the "To"/"Cc" label column — bulk mode's table
  // has no room for it and only ever edits To.
  label?: string
  list: string[]
  onAdd: (addr: string) => void
  onRemove: (addr: string) => void
  onAddMissingMasterDataEmail?: () => void
  // The address a caller's own async add/remove (e.g. bulk mode's PATCH + refetch)
  // is still mid-flight for — undefined once settled. Whether it's an add or a
  // remove is inferred from whether `list` (still the pre-round-trip data) already
  // contains it: present = pending removal (spinner replaces that chip's X);
  // absent = pending add (spinner replaces the add control).
  pendingAddress?: string
  // Drops the label column and the box padding/min-height for a single-line fit —
  // bulk mode's supplier table, one row per supplier, has no room for the
  // RecipientEditor's boxed To/Cc layout.
  compact?: boolean
}) {
  const { translate } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const pendingAdd =
    pendingAddress !== undefined && !list.includes(pendingAddress)
  // Any address on this field mid-PATCH locks the whole row, not just its own
  // chip/control — the caller (bulk mode) builds its next[] off the pre-round-trip
  // `list`, so a second add/remove fired before the first settles would compute
  // from stale data and silently undo it when both land.
  const rowLocked = pendingAddress !== undefined

  function reset() {
    setValue("")
    setError(null)
    setAdding(false)
  }

  function commit() {
    if (rowLocked) return
    const v = value.trim()
    if (!v) return reset()
    if (!EMAIL_REGEX.test(v)) {
      setError(translate("MC_INQ_DRAFT_INVALID_EMAIL"))
      return
    }
    if (list.some((a) => a.toLowerCase() === v.toLowerCase())) {
      setError(translate("MC_INQ_DRAFT_DUPLICATE_EMAIL"))
      return
    }
    onAdd(v)
    reset()
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        // Right-align each wrapped line, not just the block as a whole — otherwise
        // a second line (e.g. the "+ Add" button once chips fill the first line)
        // falls back to flex-start and looks like it drifted to the left. min-w-0
        // overrides this flex item's default `min-width: auto` so a narrow bulk-mode
        // column can actually shrink it (and let it wrap) instead of blowing out.
        compact
          ? "min-w-0 justify-end"
          : "min-h-[42px] px-3 py-2 [&+&]:border-t [&+&]:border-grey4",
      )}
    >
      {label && (
        <span className="w-7 shrink-0 text-[11px] font-semibold text-grey5">
          {label}
        </span>
      )}
      {list.map((addr, i) => (
        <span
          key={addr + i}
          className="inline-flex h-[27px] items-center gap-1.5 rounded-full border border-grey4 bg-grey3 pl-3 pr-1.5 text-[11.5px] text-white"
        >
          {addr}
          {pendingAddress === addr ? (
            <span
              role="status"
              aria-label={translate("MC_INQ_DRAFT_SAVING")}
              className="inline-flex text-grey5"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
            </span>
          ) : (
            <button
              type="button"
              aria-label={translate("MC_INQ_DRAFT_REMOVE", { addr })}
              onClick={() => onRemove(addr)}
              disabled={rowLocked}
              className={cn(
                "inline-flex text-grey5",
                rowLocked ? "cursor-not-allowed opacity-40" : "hover:text-red1",
              )}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {adding ? (
        <span className="inline-flex flex-col gap-0.5">
          <input
            autoFocus
            value={value}
            disabled={rowLocked}
            placeholder={translate("MC_INQ_DRAFT_ADD_EMAIL_PLACEHOLDER")}
            aria-invalid={!!error}
            onChange={(e) => {
              setValue(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                commit()
              }
              if (e.key === "Escape") reset()
            }}
            // Commit on blur (like a mail client's To field) instead of
            // discarding — clicking Send right after typing an address must
            // not silently drop it. commit() itself already validates and
            // keeps an invalid address visible with its error rather than
            // vanishing.
            onBlur={() => (value.trim() ? commit() : reset())}
            className={cn(
              "h-[27px] min-w-[180px] rounded-full border bg-grey3 px-3 text-[11.5px] text-white outline-hidden",
              error ? "border-red1" : "border-yellow1",
            )}
          />
          {error && (
            <span role="alert" className="px-3 text-[10.5px] text-red1">
              {error}
            </span>
          )}
        </span>
      ) : pendingAdd ? (
        <span
          role="status"
          aria-label={translate("MC_INQ_DRAFT_SAVING")}
          className="inline-flex h-[27px] items-center gap-1.5 rounded-full border border-dashed border-grey4 px-3 text-[11.5px] text-grey5"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          {translate("MC_INQ_DRAFT_SAVING")}
        </span>
      ) : (
        <button
          type="button"
          disabled={rowLocked}
          onClick={() =>
            list.length === 0 && onAddMissingMasterDataEmail
              ? onAddMissingMasterDataEmail()
              : setAdding(true)
          }
          className={cn(
            "inline-flex h-[27px] items-center gap-1 rounded-full border border-dashed border-grey4 px-3 text-[11.5px] text-grey5",
            rowLocked
              ? "cursor-not-allowed opacity-40"
              : "hover:border-grey5 hover:text-white",
          )}
        >
          <Plus className="h-3 w-3" />
          {translate("MC_INQ_DRAFT_ADD")}
        </button>
      )}
    </div>
  )
}

export function RecipientEditor({
  to,
  cc,
  setTo,
  setCc,
  supplierId,
  supplierName,
  supplierNumber,
}: RecipientEditorProps) {
  const { translate } = useTranslation()
  const [showEmailModal, setShowEmailModal] = useState(false)

  return (
    <div className="overflow-hidden rounded-lg border border-grey4 bg-grey2">
      <Row
        label={translate("MC_INQ_DRAFT_TO")}
        list={to}
        onAdd={(addr) => setTo([...to, addr])}
        onRemove={(addr) => setTo(to.filter((a) => a !== addr))}
        onAddMissingMasterDataEmail={
          supplierId ? () => setShowEmailModal(true) : undefined
        }
      />
      <Row
        label={translate("MC_INQ_DRAFT_CC")}
        list={cc}
        onAdd={(addr) => setCc([...cc, addr])}
        onRemove={(addr) => setCc(cc.filter((a) => a !== addr))}
      />
      {supplierId && (
        <AddSupplierEmailModal
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
          supplierId={supplierId}
          supplierName={supplierName ?? ""}
          supplierNumber={supplierNumber}
          onSaved={(email) => setTo([...to, email])}
        />
      )}
    </div>
  )
}
